import React, { useEffect, useState } from 'react'
import { Navigation, Radio, Truck } from 'lucide-react'

interface AnimatedTrackingBackdropProps {
  reduceMotion?: boolean
}

const ROUTES = [
  'M-80 530 C150 390 210 590 430 430 S760 180 1120 300 S1370 520 1610 310',
  'M-120 240 C180 90 340 330 560 210 S900 120 1130 470 S1400 560 1660 420',
  'M80 760 C280 560 510 730 720 540 S1050 370 1260 620 S1480 700 1640 550'
]

const AnimatedTrackingBackdrop: React.FC<AnimatedTrackingBackdropProps> = ({ reduceMotion = false }) => {
  const [systemReduced, setSystemReduced] = useState(false)

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setSystemReduced(media.matches)
    update()
    media.addEventListener?.('change', update)
    return () => media.removeEventListener?.('change', update)
  }, [])

  const paused = reduceMotion || systemReduced

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Light airy background with subtle blue tint */}
      <div className="absolute inset-0 bg-[linear-gradient(135deg,#f8fafc_0%,#eef6ff_48%,#f1f5f9_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.18),transparent_34%),radial-gradient(circle_at_82%_68%,rgba(14,165,233,0.10),transparent_28%)]" />

      {/* Faint perspective grid */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(59,130,246,.35) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,.35) 1px,transparent 1px)',
          backgroundSize: '54px 54px',
          transform: 'perspective(500px) rotateX(58deg) scale(1.5) translateY(9%)',
          transformOrigin: 'center bottom'
        }}
      />

      <svg viewBox="0 0 1536 864" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full opacity-90">
        <defs>
          <linearGradient id="track-route" x1="0" x2="1">
            <stop offset="0" stopColor="#3b82f6" stopOpacity=".18" />
            <stop offset=".5" stopColor="#2563eb" stopOpacity=".85" />
            <stop offset="1" stopColor="#f59e0b" stopOpacity=".35" />
          </linearGradient>
          <filter id="route-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {ROUTES.map((path, index) => (
          <g key={path}>
            <path d={path} fill="none" stroke="rgba(59,130,246,.18)" strokeWidth="10" />
            <path
              id={`login-route-${index}`}
              d={path}
              fill="none"
              stroke="url(#track-route)"
              strokeWidth="2.5"
              strokeDasharray="8 10"
              filter="url(#route-glow)"
            />
            {!paused && (
              <circle r="5" fill={index === 1 ? '#f59e0b' : '#3b82f6'} filter="url(#route-glow)">
                <animateMotion dur={`${13 + index * 4}s`} repeatCount="indefinite" rotate="auto">
                  <mpath href={`#login-route-${index}`} />
                </animateMotion>
              </circle>
            )}
          </g>
        ))}
        {[
          [260, 330], [510, 560], [760, 260], [1010, 465], [1280, 335]
        ].map(([cx, cy], index) => (
          <g key={`${cx}-${cy}`}>
            <circle cx={cx} cy={cy} r={paused ? 7 : 13} fill="none" stroke="#3b82f6" opacity=".35">
              {!paused && <animate attributeName="r" values="5;22;5" dur={`${3 + index * .35}s`} repeatCount="indefinite" />}
              {!paused && <animate attributeName="opacity" values=".65;0;.65" dur={`${3 + index * .35}s`} repeatCount="indefinite" />}
            </circle>
            <circle cx={cx} cy={cy} r="4" fill="#60a5fa" />
          </g>
        ))}
      </svg>

      <div className="absolute left-[8%] top-[18%] rounded-xl border border-blue-200/60 bg-white/70 backdrop-blur-md px-3 py-2 text-slate-700 shadow-xl">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
          <Radio size={12} className="text-emerald-500" />
          Live tracking
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs font-semibold text-slate-800">
          <Truck size={15} className="text-blue-600" /> 28 assets online
        </div>
      </div>
      <div className="absolute right-[9%] bottom-[16%] rounded-xl border border-blue-200/60 bg-white/70 backdrop-blur-md px-3 py-2 text-slate-700 shadow-xl">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
          <Navigation size={12} className="text-amber-500" />
          Secure corridor
        </div>
        <div className="mt-1 text-xs font-semibold text-slate-800">Route monitored</div>
      </div>

      {/* Soft vignette to keep text readable without being dark */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(255,255,255,.6)_100%)]" />
    </div>
  )
}

export default AnimatedTrackingBackdrop
